import { useSpecifyProductPlanMutation } from '@/core/api/graphql/forshawGraphqlApi.generated.ts'
import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import {
  selectDisplayValueMode,
  selectForecastItems,
  selectPlannedItems,
  selectShipmentItems,
} from '@/data/replenishmentPlan/slice/replenishmentPlan.selectors.ts'
import {
  DisplayValueMode,
  Product,
} from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'
import { EditWrapper, StyledInput, Wrapper } from './PlannedMonthCell.styles.ts'
import { getPlanFormattedDateFromDate } from '@/core/utils/getPlanFormattedDateFromDate.ts'
import { useAccountBranchIdsParams } from '@/core/hooks/useAccountBranchIdsParams.ts'
import { formatCurrency } from '@/core/utils/formatCurrency.ts'
import { Suggestion } from './Suggestion'
import { Box, ClickAwayListener, Popper } from '@mui/material'
import { numberToString } from '@/core/utils/numberToString.ts'
import { PlanHistory } from '@/ui/components/PlanHistory'
import { bindPopper, bindTrigger } from 'material-ui-popup-state'
import { usePopupState } from 'material-ui-popup-state/hooks'
import { getPlannedShippedForecastForMonth } from '@/core/utils/getPlannedShippedForecastForMonth.ts'
import { formatNumber } from '@/core/utils/formatNumber.ts'
import { CustomFade } from '@/ui/components/CustomFade'
import { isCurrentMonth } from '@/core/utils/isCurrentMonth.ts'
import { useTrackReplenishmentPlanAnalyticsEvent } from '@/core/hooks/useTrackReplenishmentPlanAnalyticsEvent.ts'
import {
  DatagridActionsEvent,
  DatagridActionsType,
} from '@/core/analytics/events/replenishmentPlan/DatagridActionsEvent.ts'

interface Props {
  date: Date
  product: Product
}

const PlannedMonthCell: FC<Props> = ({ date, product }) => {
  const { trackReplenishmentPlanAnalyticsEvent } =
    useTrackReplenishmentPlanAnalyticsEvent()

  const forecastItems = useSelector(selectForecastItems)
  const plannedItems = useSelector(selectPlannedItems)
  const shipmentItems = useSelector(selectShipmentItems)

  const formattedDate = getPlanFormattedDateFromDate(date)

  const currentMonth = isCurrentMonth(date)

  const { branchId } = useAccountBranchIdsParams()
  const inputRef = useRef<HTMLInputElement>(null)
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState<string | null>(null)

  const displayValueMode = useSelector(selectDisplayValueMode)

  const [specifyProductPlanMutation] = useSpecifyProductPlanMutation()

  const plannedValues = useMemo(() => {
    const { plannedAmount, forecast } = getPlannedShippedForecastForMonth(
      product,
      date,
      {
        forecast: forecastItems,
        planned: plannedItems,
        shipments: shipmentItems,
      },
    )

    const amount = plannedAmount ?? forecast?.amountSuggested ?? null
    const amountMinSuggested = forecast?.amountMinSuggested ?? null
    const amountMaxSuggested = forecast?.amountMaxSuggested ?? null

    const cost = amount != null ? (amount * product.price * 100) / 100 : null
    const costMinSuggested = amountMinSuggested
      ? amountMinSuggested * product.price
      : null
    const costMaxSuggested = amountMaxSuggested
      ? amountMaxSuggested * product.price
      : null

    return {
      amount,
      hasPlannedOverride: plannedAmount != null,
      amountMinSuggested,
      amountMaxSuggested,
      cost,
      costMinSuggested,
      costMaxSuggested,
    }
  }, [date, forecastItems, plannedItems, product, shipmentItems])

  const numericValue =
    (displayValueMode === DisplayValueMode.amount
      ? plannedValues.amount
      : plannedValues.cost) ?? 0
  const displayValue =
    displayValueMode === DisplayValueMode.cost
      ? formatCurrency(numericValue, { maximumFractionDigits: 0 })
      : formatNumber(numericValue)

  const onStartEdit = () => {
    const showZero = numericValue === 0 && plannedValues.hasPlannedOverride
    setValue(numericValue !== 0 || showZero ? numberToString(numericValue) : '')
    setEditing(true)
  }

  const onEditComplete = useCallback(() => {
    if (value === null || value === '' || parseFloat(value) === numericValue) {
      setEditing(false)

      return
    }

    trackReplenishmentPlanAnalyticsEvent(
      new DatagridActionsEvent({ type: DatagridActionsType.UpdatePlanned }),
    )

    let valueToSend = value

    if (displayValueMode === DisplayValueMode.cost) {
      valueToSend = (
        product.price === 0 ? 0 : parseFloat(value) / product.price
      ).toString()
    }

    specifyProductPlanMutation({
      amount: parseInt(valueToSend),
      branchId,
      month: formattedDate,
      productId: product.id,
    })

    setEditing(false)
  }, [
    branchId,
    displayValueMode,
    formattedDate,
    numericValue,
    product.id,
    product.price,
    specifyProductPlanMutation,
    trackReplenishmentPlanAnalyticsEvent,
    value,
  ])

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value.replace(/[^.0-9]/g, ''))
  }

  const onSuggestionChange = (value: number) => {
    setValue(numberToString(value))

    inputRef.current?.focus()
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      onEditComplete()
    }
  }

  const hasSuggestedValues = [
    plannedValues.amountMinSuggested,
    plannedValues.amountMaxSuggested,
    plannedValues.costMinSuggested,
    plannedValues.costMaxSuggested,
  ].some((value) => value !== null)

  const minSuggested =
    displayValueMode === DisplayValueMode.cost
      ? plannedValues.costMinSuggested
      : plannedValues.amountMinSuggested
  const maxSuggested =
    displayValueMode === DisplayValueMode.cost
      ? plannedValues.costMaxSuggested
      : plannedValues.amountMaxSuggested

  const popupState = usePopupState({
    variant: 'popper',
    popupId: 'planned-history-popover',
  })

  const popupKey = useMemo(() => {
    const productKey =
      (product as any)?.id ??
      (product as any)?.productId ??
      (product as any)?.sku ??
      'unknown'
    return `${productKey}-${date.toISOString()}-planned`
  }, [product, date])

  useEffect(() => {
    const handleOtherOpen = (event: Event) => {
      const openedId = (event as CustomEvent).detail
      if (openedId !== popupKey) {
        popupState.close()
      }
    }
    window.addEventListener(
      'plan-history-open',
      handleOtherOpen as EventListener,
    )
    return () => {
      window.removeEventListener(
        'plan-history-open',
        handleOtherOpen as EventListener,
      )
    }
  }, [popupKey, popupState])

  useEffect(() => {
    if (!editing) {
      return
    }
    // clicks outside the iframe don't reach ClickAwayListener; window blur covers them
    window.addEventListener('blur', onEditComplete)
    return () => {
      window.removeEventListener('blur', onEditComplete)
    }
  }, [editing, onEditComplete])

  const triggerProps = bindTrigger(popupState)

  return (
    <>
      <Wrapper
        {...triggerProps}
        editing={editing}
        onClick={(e) => {
          if (currentMonth) {
            return
          }

          onStartEdit()
          window.dispatchEvent(
            new CustomEvent('plan-history-open', { detail: popupKey }),
          )
          triggerProps.onClick?.(e)
        }}
        onMouseLeave={popupState.close}
        className={currentMonth ? 'currentMonth' : ''}
      >
        {editing ? (
          <ClickAwayListener
            mouseEvent="onMouseDown"
            onClickAway={onEditComplete}
          >
            <EditWrapper>
              <StyledInput
                disableUnderline
                autoFocus
                inputRef={inputRef}
                value={value}
                onKeyDown={onKeyDown}
                onChange={onChange}
              />

              {hasSuggestedValues && (
                <Suggestion
                  min={minSuggested}
                  max={maxSuggested}
                  onChange={onSuggestionChange}
                />
              )}
            </EditWrapper>
          </ClickAwayListener>
        ) : (
          <>{displayValue}</>
        )}
      </Wrapper>

      <Popper {...bindPopper(popupState)} transition placement="bottom-start">
        {({ TransitionProps }) => (
          <CustomFade {...TransitionProps} timeout={250} delay={0}>
            <Box py="2px" onMouseLeave={popupState.close}>
              <PlanHistory
                date={date}
                product={product}
                color={editing ? 'secondary' : 'primary'}
                hideActual={true}
              />
            </Box>
          </CustomFade>
        )}
      </Popper>
    </>
  )
}

export default PlannedMonthCell
